import { MouseEvent, useState } from "react";
import { Loader } from "@mantine/core";
import {
  IconArrowRight,
  IconBulb,
  IconEye,
  IconFlag,
  IconNotes,
} from "@tabler/icons-react";
import { TrackFrame } from "@/types/trackLabeling";
import { AnswerCard, AnswerCardContent, CoveredAnswerCard } from "./AnswerCard";
import { CorrectAnswerMessage } from "./correctAnswer";
import { CorrectAnswerTrackViewer } from "./CorrectAnswerTrackViewer";

// The covered card turns over into the correct answer, then glows green (keyframes in index.css).
const REVEAL_CARD_ANIMATION =
  "rounded-[20px] motion-safe:animate-[revealCard_0.55s_cubic-bezier(.2,.8,.2,1)_both,revealGlow_1.4s_ease-out_0.45s_2]";

// The message, staff note and buttons fade in once the card has turned over.
const FADE_IN_ANIMATION =
  "motion-safe:animate-[messageIn_0.35s_ease-out_0.35s_both]";

/**
 * Whether a click is the second (or later) of a double-click. "Dispute" and "Next track" buttons
 * take the "Reveal" button's place, so a double-click on it would otherwise press one of them right
 * after the reveal. Clicks from the keyboard have detail 0.
 */
const isSecondClick = (event: MouseEvent<HTMLButtonElement>) =>
  event.detail > 1;

type AnswerFeedbackProps = {
  message: CorrectAnswerMessage;
  labelerCardContent: AnswerCardContent;
  correctCardContent: AnswerCardContent;
  staffNote: string | null;
  labeledFrames: TrackFrame[];
  clearestLabeledFramePosition: number;
  isCorrectAnswerShown: boolean;
  isLocked: boolean;
  onShowCorrectAnswer: () => void;
  onDispute: () => void;
  onNextTrack: () => void;
};

/**
 * The correct answer dialog's feedback. First the track to look through again, a cue where to
 * look and the labeler's answer, with the correct answer covered. Click a button to reveal the
 * correct answer, why, the staff note, Dispute and Next track.
 */
export const AnswerFeedback = ({
  message,
  labelerCardContent,
  correctCardContent,
  staffNote,
  labeledFrames,
  clearestLabeledFramePosition,
  isCorrectAnswerShown,
  isLocked,
  onShowCorrectAnswer,
  onDispute,
  onNextTrack,
}: AnswerFeedbackProps) => {
  // Animates the reveal only when the labeler shows the correct answer here. It starts false
  // again when they come back from the dispute form, so we don't play the animation again.
  const [shouldAnimateReveal, setShouldAnimateReveal] = useState(false);

  const showCorrectAnswer = () => {
    setShouldAnimateReveal(true);
    onShowCorrectAnswer();
  };

  // Swaps the cue for the message and the covered card for the correct one at the reveal.
  const fadeInAnimation = shouldAnimateReveal ? FADE_IN_ANIMATION : "";
  const correctCardAnimation = shouldAnimateReveal ? REVEAL_CARD_ANIMATION : "";
  const cueClassName = isCorrectAnswerShown ? "invisible" : "";
  const messageClassName = isCorrectAnswerShown ? fadeInAnimation : "invisible";
  const correctCardClassName = isCorrectAnswerShown
    ? correctCardAnimation
    : "invisible";

  return (
    <div className="grid flex-1 grid-cols-2 gap-6">
      <CorrectAnswerTrackViewer
        labeledFrames={labeledFrames}
        initialFramePosition={clearestLabeledFramePosition}
      />

      <div className="flex flex-col gap-4">
        <div className="grid [&>*]:[grid-area:1/1]">
          <div
            className={`flex gap-3 rounded-[16px] border border-[#E0E7FF] bg-[#F5F7FF] px-4 py-3 ${cueClassName}`}
          >
            <IconEye size={18} className="mt-0.5 shrink-0 text-[#5A6EFA]" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[15px] font-bold text-[#1e293b]">
                Look at the track again.
              </span>
              <span className="text-[14px] leading-[1.6] text-[#475569]">
                {message.cue}
              </span>
            </div>
          </div>
          <div
            className={`flex gap-3 rounded-[16px] border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 ${messageClassName}`}
          >
            <IconBulb size={18} className="mt-0.5 shrink-0 text-[#F59E0B]" />
            <p className="m-0 text-[14px] leading-[1.6] text-[#1e293b]">
              {message.body}
            </p>
          </div>
        </div>

        <AnswerCard
          side="labeler"
          content={labelerCardContent}
          // Marks the field that differs only after the reveal: it's what to compare with the
          // correct card, which is covered until then.
          differingField={isCorrectAnswerShown ? message.differingField : null}
        />
        {/* The covered card lies over the correct one, so it has the correct card's size. */}
        <div className="grid [&>*]:[grid-area:1/1]">
          <div className={correctCardClassName}>
            <AnswerCard
              side="correct"
              content={correctCardContent}
              differingField={message.differingField}
            />
          </div>
          {!isCorrectAnswerShown && <CoveredAnswerCard />}
        </div>

        {/* Shows the staff note only after the reveal. */}
        {isCorrectAnswerShown && staffNote && (
          <div
            className={`flex gap-3 rounded-[16px] border border-[#E0E7FF] bg-[#F5F7FF] px-4 py-3 ${fadeInAnimation}`}
          >
            <IconNotes size={18} className="mt-0.5 shrink-0 text-[#5A6EFA]" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#5A6EFA]">
                Note from staff
              </span>
              <span className="text-[13px] leading-[1.55] text-[#334155]">
                {staffNote}
              </span>
            </div>
          </div>
        )}

        {isCorrectAnswerShown ? (
          // Different keys, so React doesn't reuse the reveal button's element for Dispute.
          <div
            key="answer-actions"
            className={`mt-auto flex items-center justify-end gap-3 pt-1 ${fadeInAnimation}`}
          >
            <button
              onClick={(event) => {
                if (!isSecondClick(event)) {
                  onDispute();
                }
              }}
              disabled={isLocked}
              className="flex items-center gap-2 rounded-xl border border-[#CBD5E1] bg-white px-6 py-[14px] text-[#334155] transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <IconFlag size={16} />
              <span className="mt-0.5 text-[12px] font-bold uppercase tracking-[0.12em]">
                Dispute
              </span>
            </button>
            <button
              onClick={(event) => {
                if (!isSecondClick(event)) {
                  onNextTrack();
                }
              }}
              disabled={isLocked}
              className="relative flex items-center gap-3 rounded-xl border border-transparent bg-[#5A6EFA] px-7 py-[14px] text-white shadow-sm transition-all hover:bg-[#4d5ee6] focus:outline-none focus:ring-2 focus:ring-[#5A6EFA] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <span className="mt-0.5 text-[12px] font-bold uppercase tracking-[0.12em]">
                Next track
              </span>
              {isLocked ? (
                <Loader size={18} color="white" />
              ) : (
                <IconArrowRight size={18} strokeWidth={2.5} />
              )}
            </button>
          </div>
        ) : (
          <div
            key="reveal-action"
            className="mt-auto flex items-center justify-end gap-3 pt-1"
          >
            <button
              onClick={showCorrectAnswer}
              className="flex items-center gap-3 rounded-xl border border-transparent bg-[#1e293b] px-7 py-[14px] text-white shadow-sm transition-colors hover:bg-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#1e293b] focus:ring-offset-2"
            >
              <span className="mt-0.5 text-[12px] font-bold uppercase tracking-[0.12em]">
                Show the correct answer
              </span>
              <IconArrowRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
